// © 2025 Camp Promethean, LLC. All Rights Reserved.

import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { NotificationsDropdown } from "@/components/notifications/NotificationsDropdown";
import { ThemeToggle } from "@/components/theme/ThemeToggle";
import { useAppNavigation } from "@/hooks/useAppNavigation";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/contexts/AuthContext";

export function DashboardTopBar() {
  const { breadcrumbs } = useAppNavigation();
  const isMobile = useIsMobile();
  const visibleCrumbs =
    isMobile && breadcrumbs.length > 1
      ? breadcrumbs.slice(-2)
      : breadcrumbs;
  const showEllipsis = isMobile && breadcrumbs.length > 2;

  return (
    <header className="cp-topbar safe-area-top sticky top-0 z-40 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <SidebarTrigger className="-ml-1" />
      <Separator orientation="vertical" className="mr-2 h-4" />

      <nav aria-label="Breadcrumb" className="flex min-w-0 flex-1 items-center gap-1 text-sm">
        {showEllipsis && (
          <span className="flex shrink-0 items-center gap-1 text-muted-foreground">
            <span aria-hidden>…</span>
            <ChevronRight className="h-3.5 w-3.5" />
          </span>
        )}
        {visibleCrumbs.map((crumb, index) => {
          const isLast = index === visibleCrumbs.length - 1;
          const showSeparator = index > 0 || showEllipsis;
          return (
            <span key={`${crumb.label}-${index}`} className="flex min-w-0 items-center gap-1">
              {showSeparator && (
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              )}
              {crumb.path && !isLast ? (
                <Link
                  to={crumb.path}
                  className="truncate text-muted-foreground transition-colors hover:text-foreground"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span
                  className={`truncate font-heading font-normal ${isLast ? "text-foreground" : "text-muted-foreground"}`}
                >
                  {crumb.label}
                </span>
              )}
            </span>
          );
        })}
      </nav>

      <div className="flex shrink-0 items-center gap-1">
        <PlanChip />
        <ThemeToggle />
        <NotificationsDropdown />
      </div>
    </header>
  );
}

/** Always-visible plan entry: "{Tier} · Manage" (paid) / "Free · Upgrade". */
function PlanChip() {
  const { profile } = useAuth();
  const isMobile = useIsMobile();
  const tier = profile?.membership_tier ?? "free";
  const isFree = tier === "free";

  if (isMobile || !profile) return null;

  return (
    <Link
      to={isFree ? "/pricing" : "/profile"}
      className="mr-1 flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-[11px] tracking-[0.08em] text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
      aria-label={isFree ? "Upgrade your plan" : `Manage your ${tier} plan`}
    >
      <span className={`font-medium uppercase ${isFree ? "" : "text-primary"}`}>
        {isFree ? "Free" : tier}
      </span>
      <span aria-hidden className="text-border">·</span>
      <span className="uppercase">{isFree ? "Upgrade" : "Manage"}</span>
    </Link>
  );
}
